import { useState, useEffect, type ReactElement, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import recipeService from '../lib/recipe-service';
import { recipePath } from '../lib/paths';
import { type Recipe } from '../types';
import { SearchIcon } from './icons';

/** Wait for a pause in typing before searching, rather than on every keystroke. */
const SEARCH_DELAY_MS = 250;

function Search(): ReactElement {
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [recipes, setRecipes] = useState<Recipe[]>([]);

	useEffect(() => {
		const query = searchTerm.trim();
		if (query === '') return;
		// Only the latest query may update the results. Responses can arrive out
		// of order, and a slow one for an earlier keystroke would otherwise
		// overwrite the results for what is in the box now.
		let current = true;
		const timer = setTimeout(() => {
			recipeService
				.search(query)
				.then((data: Recipe[]) => {
					if (current) setRecipes(data);
				})
				.catch((error) => console.error(error));
		}, SEARCH_DELAY_MS);
		return () => {
			current = false;
			clearTimeout(timer);
		};
	}, [searchTerm]);

	function handleSearch(e: ChangeEvent<HTMLInputElement>): void {
		const { value } = e.target;
		setSearchTerm(value);
		if (value.trim() === '') setRecipes([]);
	}

	return (
		<div>
			<div className='search'>
				<SearchIcon className='search__icon' />
				<input
					className='search__input'
					type='text'
					autoFocus
					autoComplete='off'
					name='searchTerm'
					value={searchTerm}
					placeholder='Find a recipe by name or ingredient'
					aria-label='Find a recipe'
					onChange={handleSearch}
				/>
			</div>
			{recipes.length > 0 && (
				<ul className='res-list panel' style={{ marginTop: 'var(--space-3)' }} aria-label='Search results'>
					{recipes.map((recipe: Recipe) => (
						<li key={recipe._id}>
							<Link className='res' to={recipePath(recipe)}>
								<span>
									<span className='res__name heading'>{recipe.name}</span>
									<span className='res__desc caption'>{recipe.description}</span>
								</span>
								<span className='figure muted'>{recipe.duration} min</span>
							</Link>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

export default Search;
